import { DonationFrequency } from '@web/graphql-components'
import { useDonationFormContext } from './DonationFormProvider'
import { useMemo } from 'react'

export const DONOR_PAYS_AMOUNT = 2.14

export const DONATION_DEFAULT_TITLE =
  '{"root":{"children":[{"children":[{"detail":0,"format":1,"mode":"normal","style":"font-family: \\"Helvetica Neue\\", Helvetica, Arial, sans-serif;font-size: 24px;","text":"Donate to help our planet","type":"text","version":1}],"direction":"ltr","format":"center","indent":0,"type":"paragraph","version":1},{"children":[{"detail":0,"format":0,"mode":"normal","style":"font-family: Arial, \\"Helvetica Neue\\", Helvetica, sans-serif;font-size: 16px;","text":"& Help our Earth","type":"text","version":1}],"direction":"ltr","format":"center","indent":0,"type":"paragraph","version":1}],"direction":"ltr","format":"","indent":0,"type":"root","version":1}}'

export const EMPTY_TEXT =
  '{"root":{"children":[{"children":[],"direction":null,"format":"","indent":0,"type":"paragraph","version":1}],"direction":null,"format":"","indent":0,"type":"root","version":1}}'

export const getEmptyTextWithAmount = (index: number, type: 'DonationForm' | 'DollarHandles') =>
  `{"root":{"children":[{"children":[{"detail":0,"format":0,"mode":"normal","style":"","text":"$","type":"text","version":1},{"type":"merge-tag","version":1,"value":{"MergeFieldName":"Ask ${index}","MergeFieldSettingId":"${type}.Ask${index}","MergeTag":"{{${type}.Ask${index}}}","QueryParameter":""},"format":0,"styles":{}}],"direction":null,"format":"","indent":0,"type":"paragraph","version":1}],"direction":null,"format":"","indent":0,"type":"root","version":1}}`

export const DONATION_FREQUENCY_TO_LABEL = {
  [DonationFrequency.OneTime]: 'One time',
  [DonationFrequency.Weekly]: 'Weekly',
  [DonationFrequency.Fortnightly]: 'Fortnightly',
  [DonationFrequency.Every_4Weeks]: 'Every 4 weeks',
  [DonationFrequency.Monthly]: 'Monthly',
  [DonationFrequency.Quarterly]: 'Quarterly',
  [DonationFrequency.Yearly]: 'Yearly',
}

export const useTotalAmount = () => {
  const totalAmount = useDonationFormContext(({ formValues }) => formValues.amount ?? 0)

  return totalAmount
}

export const useGivingFrequency = () => {
  const givingFrequency = useDonationFormContext(
    (context) =>
      context.formValues.givingFrequency ||
      context.settings.donationFrequency?.[context.settings.donationFrequencyDefault ?? 0] ||
      DonationFrequency.OneTime,
  )

  return givingFrequency
}

export const getNoOfInstallments = (
  givingFrequency: DonationFrequency | undefined,
  duration: number,
): [number, string] => {
  if (givingFrequency === DonationFrequency.Monthly) {
    return [duration, 'months']
  }

  if (givingFrequency === DonationFrequency.Yearly) {
    return [Math.round(duration / 12), 'years']
  }

  return [1, 'time']
}

export const useNoOfInstallments = () => {
  const givingFrequency = useDonationFormContext((state) => state.formValues.givingFrequency)
  const options = useDonationFormContext((state) => state.settings.styles?.options)

  return useMemo(
    () =>
      getNoOfInstallments(
        givingFrequency,
        Number(options?.payByInstallmentDurations?.[DonationFrequency.Monthly] || 36),
      ),
    [givingFrequency],
  )
}

export const usePayByInstallment = (): {
  minInstallmentAmount: number
  frequencies?: DonationFrequency[]
  noOfInstallments?: [number, string]
} => {
  const options = useDonationFormContext((state) => state.settings.styles?.options)
  const amount = useDonationFormContext((state) => state.formValues.amount)
  const givingFrequency = useDonationFormContext((state) => state.formValues.givingFrequency)
  const payByInstallmentOptions = useMemo(() => {
    if (!amount || !givingFrequency) {
      return null
    }

    if (!options?.payByInstallmentRanges) {
      return {
        frequencies: options?.payByInstallmentFrequencies ?? [],
        noOfInstallments: getNoOfInstallments(
          givingFrequency,
          Number(options?.payByInstallmentDurations?.[DonationFrequency.Monthly] || 36),
        ),
      }
    }

    const range = [...options.payByInstallmentRanges]
      .reverse()
      .find((r: { minAmount: number }) => amount >= Number(r.minAmount))

    if (!range) {
      return null
    }

    const noOfInstallments = getNoOfInstallments(givingFrequency, Number(range.duration))

    return {
      frequencies: range.frequencies,
      noOfInstallments,
    }
  }, [options, amount, givingFrequency])

  return {
    minInstallmentAmount: options?.payByInstallmentRanges
      ? Number(options?.payByInstallmentRanges[0].minAmount)
      : (options?.minInstallmentAmount?.[DonationFrequency.Monthly] ?? 0),
    ...payByInstallmentOptions,
  }
}
